/**
 * The weather, as the client sees it (T103 piece ④).
 *
 * **The server owns the weather** — it holds the 11 MB archive, it knows where
 * everybody is standing, and it draws the year (D57). This carries the answer
 * forward between the server's messages so the sky *moves* instead of stepping.
 *
 * ⚠️ **It never invents weather.** Before the first message it answers `null`
 * and `main.ts` falls back to the measured overcast default (V17) — which is the
 * honest Icelandic sky rather than a guess.
 *
 * ## Why this eases and `worldclock.ts` does not merely extrapolate
 *
 * The clock carries forward because time is *predictable*: the client knows the
 * rate. The weather is not predictable at all — the next reading comes out of a
 * table of real observations from a real year, and nothing on this side can
 * anticipate it. So this does the other thing: it takes each arriving reading as
 * a **target** and walks toward it.
 *
 * 🔴 **The tables step every three game hours**, which is **two and a half real
 * minutes**. Without easing, the sky would jump from four oktas to eight
 * instantly, in daylight, while the player watched. Icelandic weather changes
 * fast; it does not change in one frame.
 */

import { cloudiness, type Weather, type WeatherSnapshot } from '@lofi/shared';
import { DEFAULT_SKY_WEATHER, type SkyWeather } from './sky.ts';

/**
 * How long the sky takes to travel from one reading to the next, in real ms.
 *
 * **30 seconds**, matching the server's resync interval — so a change is always
 * complete before the next reading can arrive, and the sky is never chasing two
 * targets at once.
 *
 * ⚠️ **A judgement, and the one number here worth arguing about.** In game time
 * this is **36 minutes**, which for a front rolling in is about right. It will
 * look slow for a squall and fast for a clearing morning; there is no single
 * correct answer while the underlying data is three-hourly.
 */
const EASE_MS = 30_000;

/**
 * The precipitation, in mm, that counts as fully heavy.
 *
 * ⚠️ **A judgement.** The observation is the total that fell in the three hours
 * up to it, so 4 mm is steady rain rather than a downpour. Chosen low
 * deliberately: the visible effect is a darker sky, and Icelandic rain is
 * usually persistent rather than torrential — a scale set by cloudbursts would
 * leave ordinary rain looking like a bright day.
 */
const RAIN_FULL_MM = 4;

/** Turn a real observation into the three numbers the sky wants. */
export function toSkyWeather(weather: Weather | null): SkyWeather {
  if (!weather) return DEFAULT_SKY_WEATHER;
  return {
    cloud: cloudiness(weather),
    /* Boolean on the wire, a number here — so it can arrive gradually. */
    fog: weather.fog ? 1 : 0,
    rain: weather.rain === null ? 0 : Math.min(1, Math.max(0, weather.rain / RAIN_FULL_MM)),
  };
}

const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

export class ClientWeather {
  /** Where the ease started from, and where it is going. */
  private from: SkyWeather = DEFAULT_SKY_WEATHER;
  private to: SkyWeather = DEFAULT_SKY_WEATHER;
  /** `performance.now()` when the current ease began, or `null` before the first. */
  private startedAt: number | null = null;
  /** The last thing the server actually said — for the HUD, unsmoothed. */
  private latest: WeatherSnapshot | null = null;

  /**
   * Take the server's reading.
   *
   * ⚠️ **An identical reading does not restart the ease.** Four out of every
   * five messages carry the same table row, and restarting on each of them would
   * make a change take two and a half minutes instead of thirty seconds — the
   * sky would always be part-way to somewhere and never arrive.
   */
  sync(snapshot: WeatherSnapshot, at: number): void {
    this.latest = snapshot;
    const target = toSkyWeather(snapshot.weather);

    if (this.startedAt === null) {
      /* The first reading is not eased — there is nothing to ease from, and the
         player would watch the sky change colour immediately after joining. */
      this.from = target;
      this.to = target;
      this.startedAt = at;
      return;
    }

    if (same(target, this.to)) return;

    /* Ease from wherever we actually are, not from the previous target. A
       reading that changes twice inside one ease must not snap backwards. */
    this.from = this.skyWeather(at);
    this.to = target;
    this.startedAt = at;
  }

  /**
   * 🌌 The geomagnetic index the server last sent, or **0 before it has said**
   * (T114).
   *
   * ⚠️ **Deliberately NOT eased, unlike the cloud and the rain.** Those are
   * eased because the sky's *colour* changing in one step reads as a flicker.
   * The aurora's strength is already smoothed twice over — Kp is a three-hourly
   * average to begin with, and the curtain fades in over its own drift — so
   * easing here would be smoothing a smoothing.
   *
   * ⚠️ **0 rather than `null` when the server has not spoken**: Kp 0 is a real,
   * quiet value, and the darkness and cloud terms are what decide whether
   * anything is drawn at all.
   */
  kp(): number {
    return this.latest?.kp ?? 0;
  }

  /** The eased weather for the sky, or `null` before the server has said. */
  skyWeather(at: number): SkyWeather {
    if (this.startedAt === null) return DEFAULT_SKY_WEATHER;
    const t = Math.min(1, Math.max(0, (at - this.startedAt) / EASE_MS));
    return {
      cloud: lerp(this.from.cloud, this.to.cloud, t),
      fog: lerp(this.from.fog, this.to.fog, t),
      rain: lerp(this.from.rain, this.to.rain, t),
    };
  }

  /** Whether the server has ever said. The HUD shows a dash until it has. */
  get known(): boolean {
    return this.startedAt !== null;
  }

  /** The last reading as the server sent it — real units, for the HUD. */
  get reading(): WeatherSnapshot | null {
    return this.latest;
  }
}

function same(a: SkyWeather, b: SkyWeather): boolean {
  return a.cloud === b.cloud && a.fog === b.fog && a.rain === b.rain;
}
